/**
 * COPIED/ADAPTED from pages/dev-agent/components/metadataEditor/useMetadataEditorPanel.ts.
 *
 * Swaps performed (per the shared contract):
 *   useDevAgentStore            -> useMultiAgentIdeStore   (artifactBadges, coverageLines, setArtifactBadge)
 *   ../useUpdateMetadataItem    -> ./useUpdateMetadataItem (multi-agent base URL)
 *   ./sessionSaveWithValidation -> ./sessionSaveWithValidation (multi-agent branch-save)
 *
 * Slices the multi-agent store does NOT have (and which are therefore dropped or
 * neutralised here): `sessionUuid`, `virtualFiles`, `acceptedCodeTarget`,
 * `setPendingFile`, `setEditorOpen`. Multi-agent has no dev-agent session, so:
 *   - `virtualFiles` reads become `{}` (only `initialContent` seeds tabs)
 *   - the `sessionUuid`-gated "save locally" branch is removed — the branch save
 *     runs whenever there is content
 *   - the dev-agent `rules.cls` special-case (devAgentService user-rules) is
 *     removed (no rules.cls in the multi-agent IDE)
 * Panel state, data fetching, save/format/diff logic for the multi-agent editor.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type * as monaco from "monaco-editor";
import { getFormatApiLanguage } from "@ncoder/ui";
import type { EditorTab, FormatApi, MonacoEditorTheme } from "@ncoder/code-editor";
import {
  useActiveBranch,
  useSaveBranchFile,
  useAbandonBranch,
  useDeleteBranchFile,
} from "@/hooks/metadata_v1";
import { useGitFile, useGitSelectedRepo, useDefaultGitBranch, useGitConnection } from "@/hooks/git";
import { useMultiAgentMetadataItem } from "./useMultiAgentMetadataItem";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useUpdateMetadataItem } from "./useUpdateMetadataItem";
import { useFormatCode } from "@/hooks/metadata_v1/useFormatCode";
import { useAutoFixLoop } from "@/hooks/chat";
import { useMultiAgentApexLint, EMPTY_ANALYSIS_MARKERS } from "./useMultiAgentEditorChecks";
import { useCreateMetadataItem } from "@/hooks/chat/useCreateMetadataItem";
import { useMetadataProgressStore } from "@/stores/metadataProgressStore";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { graphqlRequest } from "@/services/deployment/client";
import { SEARCH_METADATA_ITEMS_QUERY } from "@/services/metadata_v1/queries";
import toast from "react-hot-toast";
import { useTheme } from "next-themes";
import { runSessionSaveWithValidation } from "./sessionSaveWithValidation";
import { resolveApexClassName } from "./apexClassName";
import type { MetadataEditorPanelProps } from "./types";
import { disposeStandaloneDiffEditor } from "./disposeStandaloneDiffEditor";
import { buildCoverageMarkers } from "./buildCoverageMarkers";
import { buildPackageTabs } from "./buildPackageTabs";
import { METADATA_TABBED_EDITOR_OPTIONS } from "./metadataTabbedEditorOptions";
import { isHexDigit } from "@/services/multi-agent";

/**
 * Backend metadata_item ids are UUIDs; chat-seeded tabs still use this shape.
 * Canonical UUID: 8-4-4-4-12 hex digits, hyphens at positions 8/13/18/23,
 * total length 36, case-insensitive. (Local copy — the sibling
 * resolveDependencyItemForEditor.ts keeps its own; no cross-import.)
 */
function _isCanonicalMetadataItemId(id: string): boolean {
  if (id.length !== 36) return false;
  for (let i = 0; i < 36; i++) {
    const ch = id[i];
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      if (ch !== "-") return false;
    } else if (!isHexDigit(ch)) {
      return false;
    }
  }
  return true;
}

const METADATA_TYPE_EXTENSIONS: Record<string, string> = {
  ApexClass: ".cls",
  ApexTrigger: ".trigger",
  ApexPage: ".page",
  AuraDefinitionBundle: ".cmp",
  LightningComponentBundle: ".js",
  Flow: ".flow",
  CustomObject: ".object",
  PermissionSet: ".permissionset",
};

const SFDX_DIR: Record<string, string> = {
  ApexClass: "classes",
  ApexTrigger: "triggers",
  LightningComponentBundle: "lwc",
  AuraDefinitionBundle: "aura",
  Flow: "flows",
  CustomObject: "objects",
  ApexPage: "pages",
  ApexComponent: "components",
  StaticResource: "staticresources",
  PermissionSet: "permissionsets",
  Profile: "profiles",
  Layout: "layouts",
  CustomTab: "tabs",
};

function buildGithubPath(metadataType: string | undefined, fileName: string): string {
  const dir = SFDX_DIR[metadataType ?? ""] ?? metadataType?.toLowerCase() ?? "unknown";
  return `force-app/main/default/${dir}/${fileName}`;
}

/** Multi-agent store has no `virtualFiles` slice — use an empty map everywhere
 *  the dev-agent panel read `useDevAgentStore(s => s.virtualFiles)`. */
const EMPTY_VIRTUAL_FILES: Record<
  string,
  { content: string; metadataType: string; name: string }
> = {};

// Class-name resolution (declaration-in-source wins, tab name is fallback)
// lives in ./apexClassName — extracted for unit testability after prod saves
// ran under comment-derived names (`for`, `Name`) on 2026-08-19.

function _fileNameForNewMetadata(fileStem: string, metadataType: string): string {
  const ext = METADATA_TYPE_EXTENSIONS[metadataType] || "";
  return ext && !fileStem.endsWith(ext) ? `${fileStem}${ext}` : fileStem;
}

export function useMetadataEditorPanel({
  openFiles,
  activeFileId,
  onActiveFileChange,
  onCloseTab,
  onClose,
  acceptedCode,
  onAcceptedCodeChange,
  onPendingFileChange: _onPendingFileChange,
  initialContent,
  onFileSaved,
  orgId,
  onLoadIntoChat,
  extraActions,
  isAnalyzing,
  onRunTest,
  isBusy,
  onCanonicalFileId,
  onPromoteTab,
}: MetadataEditorPanelProps) {
  const { resolvedTheme } = useTheme();
  const editorTheme: MonacoEditorTheme =
    resolvedTheme === "dark" ? "apex-dark" : "apex-light";

  const diffEditorRef = useRef<monaco.editor.IStandaloneDiffEditor | null>(null);

  const contentByFileIdRef = useRef<Record<string, string>>({});
  const activeFileIdRef = useRef<string | null>(null);
  const autoFixRef = useRef<{
    startAutoFix: (code: string, errorMessage: string) => void;
  } | null>(null);
  const [saveErrors, setSaveErrors] = useState<{
    message: string;
    code: string;
  } | null>(null);
  const [contentByFileId, setContentByFileId] = useState<Record<string, string>>(
    {},
  );
  const [dirtyByFileId, setDirtyByFileId] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [errorExpanded, setErrorExpanded] = useState(false);
  const [diffOverlayOpen, setDiffOverlayOpen] = useState(false);
  const [diffHunkTotal, setDiffHunkTotal] = useState(0);
  const [diffHunkResolved, setDiffHunkResolved] = useState(0);
  const [originalByFileId, setOriginalByFileId] = useState<
    Record<string, string>
  >({});
  const [viewingMain, setViewingMain] = useState(false);
  const [viewingGithub, setViewingGithub] = useState(false);

  const activeFile = useMemo(
    () => openFiles.find((f) => f.id === activeFileId) ?? null,
    [openFiles, activeFileId],
  );
  const selectedOrg = useAuthStoreV1((s) => s.selectedOrg);
  const selectedRetrievalId = useMetadataProgressStore((s) => s.selectedRetrievalId);

  // ── GitHub view ──
  const { data: gitConnection } = useGitConnection();
  const isGithubConnected = gitConnection != null;
  const { data: selectedGitRepo, status: repoStatus } = useGitSelectedRepo({ enabled: !!selectedOrg?.id });
  const { data: defaultGitBranch } = useDefaultGitBranch();
  const gitRef = defaultGitBranch?.name ?? selectedGitRepo?.default_branch;
  const githubPath = activeFile ? buildGithubPath(activeFile.metadataType, activeFile.name) : null;
  const { data: githubFile, isLoading: isLoadingGithub, isFetched: isGithubFetched } = useGitFile(
    viewingGithub && selectedGitRepo ? selectedGitRepo.full_name : null,
    viewingGithub ? githubPath : null,
    gitRef,
  );
  const githubNoRepo = viewingGithub && repoStatus === "success" && selectedGitRepo == null;
  const githubNotFound = !githubNoRepo && viewingGithub && isGithubFetched && !isLoadingGithub && githubFile == null;
  const githubContent = githubFile?.content ?? null;

  const artifactBadges = useMultiAgentIdeStore((s) => s.artifactBadges);
  const setArtifactBadge = useMultiAgentIdeStore((state) => state.setArtifactBadge);
  const activeBadge = activeFileId ? artifactBadges[activeFileId] : undefined;

  // UUID of the source artifact the active (proposed) file was derived from
  // (`artifact_created.metadata.source_artifact_id`). Drives MultiAgentCompareView,
  // which resolves the ORIGINAL via GET /api/artifacts/{id}. `undefined` => no
  // multi-agent baseline; Compare uses the in-hand originalContentForDiff instead.
  const sourceArtifactIds = useMultiAgentIdeStore((s) => s.sourceArtifactIds);
  const baseSourceArtifactId = activeFileId
    ? (sourceArtifactIds[activeFileId] ?? null)
    : null;

  // MA-17: the tab the current acceptedCode belongs to. showDiff is gated on this
  // matching the active file so a stale acceptedCode never opens the diff on the
  // wrong tab. When unset (e.g. older accept paths) we treat any non-empty
  // acceptedCode for the active tab as valid (back-compat).
  const acceptedCodeTarget = useMultiAgentIdeStore((s) => s.acceptedCodeTarget);
  const acceptedCodeOriginal = useMultiAgentIdeStore((s) => s.acceptedCodeOriginal);
  const acceptedCodeOperation = useMultiAgentIdeStore((s) => s.acceptedCodeOperation);
  // A delete's acceptedCode is "" BY DESIGN (a destructiveChanges removal has no
  // new body) — this is the single source of truth for "the pending diff on
  // THIS tab is a delete", consumed by both showDiff/showTabbedEditor below and
  // handleSave's branch to the delete mutation.
  const isDeleteTarget =
    acceptedCodeOperation === "deleted" &&
    acceptedCodeTarget != null &&
    acceptedCodeTarget === activeFileId;

  // Multi-agent has no dev-agent session and no virtual-file store slice.
  const virtualFiles = EMPTY_VIRTUAL_FILES;
  const hasSeededChatContent = !!(activeFileId && initialContent?.[activeFileId]);
  // Session artifact ids (`art_…`) are never GraphQL metadata_item UUIDs — treat
  // them as virtual even before `initialContent` is seeded so `useActiveBranch`
  // doesn't fire with a non-UUID id the backend rejects.
  const isArtifactSessionId = !!(
    activeFileId && activeFileId.startsWith("art_")
  );
  // Run-produced artifacts use BARE-UUID ids (not `art_…`) and live in the
  // artifacts table, NOT metadata_items — so a metadata-item-by-id probe 404s.
  // When such a tab is open AND its content is already seeded (workspace click →
  // setWorkspaceCodeRequest), treat it as virtual so the editor shows the seed
  // and never fires the metadata fetch. If somehow unseeded, this stays false
  // and getMetadataItem's artifact fallback still recovers the content.
  const runArtifacts = useMultiAgentStore((s) => s.artifacts);
  const isKnownRunArtifact = !!(
    activeFileId && runArtifacts.some((a) => a.id === activeFileId)
  );
  // Only treat as "virtual" when the tab id is not a real metadata UUID. Seeded
  // content for UUID tabs must still load main + active branch from the API.
  const isVirtualFile = !!(
    isArtifactSessionId ||
    (isKnownRunArtifact && hasSeededChatContent) ||
    (activeFileId &&
      hasSeededChatContent &&
      !_isCanonicalMetadataItemId(activeFileId))
  );
  const {
    data: metadataFileData,
    isLoading: isLoadingMetadataFile,
    error: metadataFileError,
    refetch: refetchMetadataFile,
  } = useMultiAgentMetadataItem(activeFileId, {
    enabled: !!activeFileId && !isVirtualFile,
  });

  const { data: activeBranch } = useActiveBranch(activeFileId, {
    enabled: !!activeFileId && !isVirtualFile,
  });

  // Before a branch exists, viewing GitHub means the user wants to start
  // editing FROM the GitHub content — adopt it as the working buffer once it
  // loads. Guarded on !dirty so it never clobbers in-progress edits.
  useEffect(() => {
    if (!viewingGithub || activeBranch || !activeFileId || !githubFile) return;
    if (dirtyByFileId[activeFileId]) return;
    if (contentByFileId[activeFileId] === githubFile.content) return;
    setContentByFileId((prev) => ({ ...prev, [activeFileId]: githubFile.content }));
    setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: githubFile.content }));
  }, [viewingGithub, activeBranch, activeFileId, githubFile, dirtyByFileId, contentByFileId]);

  const { mutateAsync: createMetadataItemAsync } = useCreateMetadataItem();

  const { mutateAsync: saveBranchAsync, isPending: isBranchSaving } = useSaveBranchFile({
    onSuccess: (branch) => {
      toast.success("Saved");
      if (activeFileId) {
        setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: false }));
        setOriginalByFileId((prev) => ({
          ...prev,
          [activeFileId]:
            branch.content ?? contentByFileIdRef.current[activeFileId] ?? "",
        }));
        // Land on "Local" after a successful save, whichever origin it came from.
        setViewingGithub(false);
      }
      if (branch.metadataItem?.fileName && branch.metadataItem?.metadataType) {
        onFileSaved?.({
          id: branch.metadataItem.id,
          fileName: branch.metadataItem.fileName,
          metadataType: branch.metadataItem.metadataType,
        });
      }
    },
    onError: (error) => toast.error(error.message || "Failed to save"),
  });

  const { mutateAsync: deleteBranchAsync, isPending: isDeletingBranch } = useDeleteBranchFile({
    onSuccess: () => {
      toast.success("Deleted");
      if (activeFileId) {
        setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: false }));
        setViewingGithub(false);
      }
    },
    onError: (error) => toast.error(error.message || "Failed to delete"),
  });

  const originalByFileIdRef = useRef<Record<string, string>>({});
  originalByFileIdRef.current = originalByFileId;

  const { mutate: abandonBranch, isPending: isAbandoning } = useAbandonBranch(
    activeFileId,
    {
      onSuccess: () => {
        const id = activeFileIdRef.current;
        if (id) {
          const main = originalByFileIdRef.current[id] ?? "";
          setContentByFileId((prev) => ({ ...prev, [id]: main }));
          setDirtyByFileId((prev) => ({ ...prev, [id]: false }));
          setViewingMain(false);
        }
        toast.success("Branch discarded");
      },
      onError: (error) => toast.error(error.message || "Failed to discard"),
    },
  );

  const saveResolveRef = useRef<(() => void) | null>(null);
  const saveRejectRef = useRef<((err: Error) => void) | null>(null);
  /** Blocks concurrent saves before React re-renders `isSaving` (double Save / Cmd+S). */
  const saveInFlightRef = useRef(false);
  // Stop button: flips true to bail the validate/auto-fix loop; reset on each save.
  const saveAbortRef = useRef(false);
  // Cancels the IN-FLIGHT validate/fix request so Stop interrupts a slow call
  // immediately instead of waiting for it to return.
  const saveAbortControllerRef = useRef<AbortController | null>(null);
  const abortSave = useCallback(() => {
    saveAbortRef.current = true;
    saveAbortControllerRef.current?.abort();
  }, []);

  contentByFileIdRef.current = contentByFileId;
  activeFileIdRef.current = activeFileId;

  const updateMetadataMutation = useUpdateMetadataItem({
    onSuccess: (data) => {
      if (saveResolveRef.current) {
        saveResolveRef.current();
        saveResolveRef.current = null;
        saveRejectRef.current = null;
      }
      toast.success("Code saved successfully");
      if (activeFileId) {
        setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: false }));
        setOriginalByFileId((prev) => ({
          ...prev,
          [activeFileId]: contentByFileId[activeFileId] ?? "",
        }));
        refetchMetadataFile();
      }
      if (data?.fileName && data?.metadataType) {
        onFileSaved?.({
          id: data.id,
          fileName: data.fileName,
          metadataType: data.metadataType,
        });
      }
    },
    onError: (error) => {
      if (saveRejectRef.current) {
        saveRejectRef.current(error);
        saveResolveRef.current = null;
        saveRejectRef.current = null;
        return;
      }
      const currentId = activeFileIdRef.current;
      const currentCode = currentId
        ? (contentByFileIdRef.current[currentId] ?? "")
        : "";
      // The old run lane's `fix-code` route is gone and Lane V2 has no per-file equivalent: surface the error as it
      // arrived (the reader edits by hand) rather than handing this surface to the external auto-fix service.
      if (currentId && currentCode) {
        toast.error(error.message || "Failed to save code");
        return;
      }
      if (currentId && currentCode && autoFixRef.current?.startAutoFix) {
        autoFixRef.current.startAutoFix(
          currentCode,
          error.message || "Compilation failed",
        );
        return;
      }
      toast.error(error.message || "Failed to save code");
    },
  });

  const getReferenceFiles = useCallback(() => {
    const id = activeFileIdRef.current;
    const content = contentByFileIdRef.current;
    return openFiles
      .filter((f) => f.id !== id)
      .map((f) => ({ file_name: f.name, content: content[f.id] ?? "" }))
      .filter((r) => r.content.length > 0);
  }, [openFiles]);

  const { autoFixState, startAutoFix, cancelAutoFix, resetAutoFix } =
    useAutoFixLoop({
      maxAttempts: 5,
      onCodeFixed: (fixedCode) => {
        if (activeFileId) {
          setContentByFileId((prev) => ({ ...prev, [activeFileId]: fixedCode }));
          setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
        }
      },
      onSave: () =>
        new Promise<void>((resolve, reject) => {
          if (!activeFileId) {
            reject(new Error("No active file"));
            return;
          }
          saveResolveRef.current = resolve;
          saveRejectRef.current = reject;
          updateMetadataMutation.mutate({
            id: activeFileId,
            content: contentByFileId[activeFileId] ?? "",
          });
        }),
      fileName: activeFile?.name,
      metadataType: activeFile?.metadataType,
      orgId,
      getReferenceFiles,
    });

  useEffect(() => {
    autoFixRef.current = { startAutoFix };
  }, [startAutoFix]);

  // Multi-agent editor checks: inline lint/analysis are no-ops (the dev-agent
  // ide-service is not part of this surface); the real compile gate is on save.
  const lintApi = useMultiAgentApexLint();
  const analysisMarkers = EMPTY_ANALYSIS_MARKERS;

  const covData = useMultiAgentIdeStore((s) => s.coverageLines);
  const coverageMarkers = useMemo(
    () => buildCoverageMarkers(covData, activeFile),
    [covData, activeFile],
  );

  useEffect(() => {
    resetAutoFix();
  }, [activeFileId, resetAutoFix]);

  useEffect(() => {
    setViewingMain(false);
    setViewingGithub(false);
  }, [activeFileId]);

  const activeInitialContent = activeFileId
    ? initialContent?.[activeFileId]
    : undefined;
  useEffect(() => {
    if (!activeFileId) return;

    if (activeInitialContent !== undefined) {
      const ic = activeInitialContent;
      setContentByFileId((prev) => {
        if (prev[activeFileId] !== undefined) return prev;
        return { ...prev, [activeFileId]: ic };
      });
      // Do NOT set originalByFileId or dirtyByFileId here for AI-seeded files.
      // originalByFileId: we have no "saved" baseline until after the first
      //   successful save (onSuccess sets it then).
      // dirtyByFileId: opening a file should not immediately enable Save.
      //   @monaco-editor/react suppresses onChange on programmatic value updates
      //   (confirmed by Q.current guard in its source), so handleContentChange
      //   only fires on real user keystrokes. The first keystroke sets dirty=true.
      //   AcceptedCodeDiffSection.tsx also sets dirty=true on hunk Accept.
      return;
    }

    if (metadataFileData?.content === undefined) return;
    const mainContent = metadataFileData.content ?? "";
    const displayContent = activeBranch
      ? (activeBranch.content ?? mainContent)
      : mainContent;
    setContentByFileId((prev) => {
      if (prev[activeFileId] !== undefined) return prev;
      return { ...prev, [activeFileId]: displayContent };
    });
    // When activeBranch changes (e.g. after save creates a new branch),
    // update the original to reflect the branch baseline so Compare doesn't
    // show stale diffs.
    const baseline = activeBranch ? (activeBranch.content ?? mainContent) : mainContent;
    setOriginalByFileId((prev) => {
      if (prev[activeFileId] === undefined) {
        return { ...prev, [activeFileId]: baseline };
      }
      if (activeBranch && prev[activeFileId] !== baseline) {
        return { ...prev, [activeFileId]: baseline };
      }
      return prev;
    });
  }, [activeFileId, activeInitialContent, metadataFileData?.content, activeBranch]);

  useEffect(() => {
    if (activeFileId === null && openFiles.length > 0) {
      onActiveFileChange(openFiles[0].id);
    }
  }, [activeFileId, openFiles, onActiveFileChange]);

  const handleClose = useCallback(() => {
    const anyDirty = Object.values(dirtyByFileId).some(Boolean);
    if (anyDirty && !window.confirm("You have unsaved changes. Close anyway?")) {
      return;
    }
    disposeStandaloneDiffEditor(diffEditorRef);
    onAcceptedCodeChange(null);
    onClose();
  }, [dirtyByFileId, onClose, onAcceptedCodeChange]);

  const handleToggleViewMain = useCallback(() => {
    if (!activeBranch || !activeFileId) return;
    setViewingMain((prev) => !prev);
  }, [activeBranch, activeFileId]);

  const handleToggleViewGithub = useCallback(() => {
    if (viewingGithub) {
      setViewingGithub(false);
    } else {
      if (viewingMain) setViewingMain(false);
      setViewingGithub(true);
    }
  }, [viewingGithub, viewingMain]);

  const handleAbandon = useCallback(() => {
    if (!activeBranch) return;
    abandonBranch({ branchId: activeBranch.id });
  }, [activeBranch, abandonBranch]);

  const handleSave = useCallback(async () => {
    if (
      !activeFileId ||
      isSaving ||
      isBranchSaving ||
      isDeletingBranch ||
      viewingMain ||
      (viewingGithub && !!activeBranch)
    )
      return;
    if (saveInFlightRef.current) return;

    // A delete has no new body to save — the SAME action (whatever the caller
    // labels "Save"/"Apply") stages a removal instead of a content update, so
    // there is no second "delete" control to keep in sync.
    if (isDeleteTarget) {
      saveInFlightRef.current = true;
      try {
        const className = resolveApexClassName(activeFile?.name || activeFileId);
        const metadataType = activeFile?.metadataType || "ApexClass";
        const retrievalId = selectedOrg?.latestMetadataRetrieval?.id || selectedRetrievalId;
        if (!selectedOrg?.id || !retrievalId) {
          toast.error("No metadata retrieval found. Retrieve metadata first.");
          return;
        }
        let metadataItemId: string | null = null;
        try {
          const search = await graphqlRequest<{
            searchMetadataItems: Array<{ id: string; fullName: string; metadataType: string }>;
          }>(SEARCH_METADATA_ITEMS_QUERY, {
            organizationId: selectedOrg.id,
            retrievalId,
            searchString: className,
          });
          metadataItemId =
            search.searchMetadataItems?.find(
              (i) => i.fullName === className && i.metadataType === metadataType,
            )?.id ?? null;
        } catch {
          metadataItemId = null;
        }
        if (!metadataItemId) {
          toast.error(`Could not resolve ${className} to delete.`);
          return;
        }
        await deleteBranchAsync({ metadataItemId });
      } finally {
        saveInFlightRef.current = false;
      }
      return;
    }

    const content =
      contentByFileId[activeFileId] ?? metadataFileData?.content;
    if (content == null) {
      toast.error("No content to save");
      return;
    }

    saveInFlightRef.current = true;
    saveAbortRef.current = false;
    saveAbortControllerRef.current = new AbortController();
    try {
      const className = resolveApexClassName(
        activeFile?.name || activeFileId,
        typeof content === "string" ? content : undefined,
      );
      const metadataType = activeFile?.metadataType || "ApexClass";

      // Resolve the real ncoder-backend metadata_item id by CLASS NAME — the
      // reliable identifier. The editor tab id can be an ARTIFACT id (when a
      // refactor diff was opened via Compare), which is NOT a metadata_item, so
      // saving against it fails with "Metadata item not found". Search by name
      // first; for virtual (AI-generated) files that don't exist yet, create the
      // row then search. Available for ALL files (used as the save fallback).
      const ensureMetadataItemId = async (): Promise<string | null> => {
        const retrievalId =
          selectedOrg?.latestMetadataRetrieval?.id || selectedRetrievalId;
        if (!selectedOrg?.id || !retrievalId) {
          if (isVirtualFile) {
            toast.error(
              "No metadata retrieval found. Retrieve metadata first before saving a new class.",
            );
          }
          return null;
        }
        const lookup = async (): Promise<string | null> => {
          // The search resolver throws ("Metadata item not found") when the
          // file id is virtual/unlinked (auto-fixed code with no real metadata
          // id). Degrade gracefully: treat any lookup failure as "no existing
          // item" so the caller proceeds as create/virtual instead of letting
          // the throw escape to the console.
          let search: {
            searchMetadataItems: Array<{
              id: string;
              fullName: string;
              metadataType: string;
            }>;
          };
          try {
            search = await graphqlRequest<{
              searchMetadataItems: Array<{
                id: string;
                fullName: string;
                metadataType: string;
              }>;
            }>(SEARCH_METADATA_ITEMS_QUERY, {
              organizationId: selectedOrg.id,
              retrievalId,
              searchString: className,
            });
          } catch {
            return null;
          }
          return (
            search.searchMetadataItems?.find(
              (i) => i.fullName === className && i.metadataType === metadataType,
            )?.id ?? null
          );
        };
        let resolvedId = await lookup();
        if (!resolvedId && isVirtualFile) {
          await createMetadataItemAsync({
            organizationId: selectedOrg.id,
            retrievalId,
            metadataType,
            fullName: className,
            fileName: _fileNameForNewMetadata(className, metadataType),
            content,
            deployToSalesforce: false,
          });
          resolvedId = await lookup();
        }
        return resolvedId;
      };

      await runSessionSaveWithValidation({
        activeFileId,
        className,
        metadataType,
        content,
        activeBranch,
        saveBranchAsync,
        ensureMetadataItemId,
        setIsSaving,
        setSaveErrors,
        setContentByFileId,
        setDirtyByFileId,
        setOriginalByFileId,
        onFileSaved,
        onTabIdResolved: onCanonicalFileId,
      });
    } finally {
      saveInFlightRef.current = false;
    }
  }, [
    activeFileId,
    contentByFileId,
    metadataFileData?.content,
    activeFile,
    onFileSaved,
    isSaving,
    isBranchSaving,
    isDeletingBranch,
    isDeleteTarget,
    deleteBranchAsync,
    viewingMain,
    viewingGithub,
    activeBranch,
    saveBranchAsync,
    createMetadataItemAsync,
    isVirtualFile,
    selectedOrg,
    selectedRetrievalId,
    onCanonicalFileId,
  ]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      disposeStandaloneDiffEditor(diffEditorRef);
      onActiveFileChange(tabId);
    },
    [onActiveFileChange],
  );
  const handleTabClose = useCallback(
    (tabId: string) => {
      disposeStandaloneDiffEditor(diffEditorRef);
      onCloseTab(tabId);
    },
    [onCloseTab],
  );
  const handleContentChange = useCallback((tabId: string, value: string) => {
    // Typing in a preview tab makes it permanent, as in VS Code. This is also what
    // keeps the preview slot safe to recycle: a tab with unsaved work is never a
    // preview, so replacing "the preview" can never discard edits.
    onPromoteTab?.(tabId);
    setContentByFileId((prev) => ({ ...prev, [tabId]: value }));
    setDirtyByFileId((prev) => {
      const original = originalByFileIdRef.current[tabId];
      // @monaco-editor/react suppresses onChange on programmatic value updates
      // (Q.current guard), so this callback only fires on real user keystrokes.
      //
      // No saved baseline (AI-seeded or new file): any keystroke means dirty.
      // With a saved baseline (regular file or post-save): compare to detect
      // real changes — this also lets undo back to saved state clear dirty.
      const isDirty = original === undefined ? true : value !== original;
      if (prev[tabId] === isDirty) return prev;
      return { ...prev, [tabId]: isDirty };
    });
    if (artifactBadges[tabId] === "R") {
      setArtifactBadge(tabId, "M");
    }
  }, [artifactBadges, setArtifactBadge]);

  const { mutateAsync: formatCodeAsync } = useFormatCode();
  const formatApi: FormatApi | undefined = useMemo(() => {
    if (!activeFile) return undefined;
    return {
      format: async (code: string, _language: string) => {
        const formatLang = getFormatApiLanguage(
          activeFile.metadataType,
          activeFile.name,
        );
        if (!formatLang) {
          toast.error("Format not supported for this file type");
          throw new Error("Unsupported format language");
        }
        const result = await formatCodeAsync({ code, language: formatLang });
        if (result.error) toast.error(`Format: ${result.error}`);
        return result.formatted;
      },
    };
  }, [activeFile, formatCodeAsync]);

  const packageTabs: EditorTab[] = useMemo(
    () =>
      buildPackageTabs({
        openFiles,
        activeFileId,
        viewingMain,
        contentByFileId,
        dirtyByFileId,
        originalByFileId,
        initialContent,
        virtualFiles,
      }),
    [
      openFiles,
      activeFileId,
      viewingMain,
      contentByFileId,
      dirtyByFileId,
      originalByFileId,
      initialContent,
      virtualFiles,
    ],
  );

  // dirtyByFileId is the single source of truth for save-button state.
  // initialContent-seeded files get dirty=true set in the loading useEffect
  // above, so there's no need to fall back to !!initialContent here. Falling
  // back to initialContent caused save to stay enabled after a successful save
  // + panel remount (dirtyByFileId resets to {} on unmount, but initialContent
  // persists in the parent bridge state).
  const isDirty =
    activeFileId && !viewingMain && !(viewingGithub && !!activeBranch)
      ? (dirtyByFileId[activeFileId] ?? false)
      : false;
  // A refactor diff artifact carries its own ORIGINAL inside the artifact
  // (acceptedCodeOriginal). When present for THIS tab, use it as the Compare
  // baseline and suppress sourceArtifactId so Compare doesn't fetch the wrong
  // (blast-radius) source artifact.
  const usingInHandDiffOriginal =
    acceptedCodeOriginal != null &&
    acceptedCodeTarget != null &&
    acceptedCodeTarget === activeFileId;
  const sourceArtifactId = usingInHandDiffOriginal ? null : baseSourceArtifactId;
  const originalContentForDiff = usingInHandDiffOriginal
    ? acceptedCodeOriginal
    : activeFileId
      ? (originalByFileId[activeFileId] ??
        metadataFileData?.content ??
        initialContent?.[activeFileId] ??
        "")
      : "";

  // isDeleteTarget (derived above, near acceptedCodeOperation) is the single
  // source of truth: a delete's acceptedCode is "" BY DESIGN, and that must
  // still open the diff, not fall through to the plain tabbed editor as if
  // nothing were pending.
  const showDiff =
    (!metadataFileError ||
      isVirtualFile ||
      contentByFileId[activeFileId ?? ""] !== undefined) &&
    acceptedCode != null &&
    (acceptedCode !== "" || isDeleteTarget) &&
    !!activeFileId &&
    // MA-17: only open the diff when the acceptedCode belongs to THIS tab. When
    // no target was recorded, fall back to the old behaviour (back-compat).
    (acceptedCodeTarget == null || acceptedCodeTarget === activeFileId);

  const showTabbedEditor =
    (!metadataFileError ||
      isVirtualFile ||
      contentByFileId[activeFileId ?? ""] !== undefined) &&
    (acceptedCode == null || (acceptedCode === "" && !isDeleteTarget));

  return {
    shouldRenderNull: openFiles.length === 0,
    editorTheme,
    diffEditorRef,
    saveErrors,
    setSaveErrors,
    abortSave,
    contentByFileId,
    setContentByFileId,
    setOriginalByFileId,
    setDirtyByFileId,
    isSaving,
    errorExpanded,
    setErrorExpanded,
    diffOverlayOpen,
    setDiffOverlayOpen,
    diffHunkTotal,
    diffHunkResolved,
    setDiffHunkTotal,
    setDiffHunkResolved,
    viewingMain,
    viewingGithub,
    isGithubConnected,
    handleToggleViewGithub,
    isLoadingGithub,
    githubContent,
    githubNoRepo,
    githubNotFound,
    selectedGitRepo,
    gitRef,
    activeFile,
    activeBadge,
    sourceArtifactId,
    virtualFiles,
    isVirtualFile,
    metadataFileData,
    isLoadingMetadataFile,
    metadataFileError,
    activeBranch,
    handleClose,
    handleToggleViewMain,
    handleAbandon,
    handleSave,
    handleTabChange,
    handleTabClose,
    handleContentChange,
    formatApi,
    lintApi,
    packageTabs,
    isDirty,
    originalByFileId,
    originalContentForDiff,
    showDiff,
    showTabbedEditor,
    autoFixState,
    cancelAutoFix,
    resetAutoFix,
    analysisMarkers,
    coverageMarkers,
    covData,
    isAbandoning,
    isBranchSaving: isBranchSaving || isDeletingBranch,
    editorOptions: METADATA_TABBED_EDITOR_OPTIONS,
    extraActions,
    isAnalyzing,
    onRunTest,
    isBusy,
    onLoadIntoChat,
    acceptedCode,
    onAcceptedCodeChange,
  };
}
